import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UsersIcon, PlusIcon, ViewIcon, DownloadIcon, DocumentIcon, CalendarIcon, BanknotesIcon, FileCheckIcon, ChevronRightIcon } from './BorrowingsIcons';
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

export const LenderManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddLenderDialog, setShowAddLenderDialog] = useState(false);
  const [selectedLender, setSelectedLender] = useState<number | null>(null);
  const [mainView, setMainView] = useState<"lenders" | "lender-details">("lenders");
  const [selectedTab, setSelectedTab] = useState("contacts");
  
  // Report generation states
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [showFileReportDialog, setShowFileReportDialog] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<{ 
    id: number; 
    name: string; 
    date: string; 
    type: string; 
    fileUrl: string;
    lenderId: number | null;
  } | null>(null);
  const [filingDate, setFilingDate] = useState("");
  
  // New state for template file
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [showUploadTemplateDialog, setShowUploadTemplateDialog] = useState(false);
  
  // Sample data - would be fetched from an API in a real application
  const lenders = [
    { 
      id: 1, 
      name: "HDFC Bank", 
      type: "Bank", 
      activeLoans: 3, 
      totalExposure: 1200000000,
      keyContacts: [
        { name: "Rahul Sharma", designation: "Relationship Manager", email: "rahul.s@hdfcbank.com" },
        { name: "Priya Patel", designation: "Credit Officer", email: "priya.p@hdfcbank.com" }
      ],
      recentEvents: [
        { date: "2025-03-15", description: "Interest rate reduced by 25 bps on Loan #HDL-2023-001" },
        { date: "2025-02-10", description: "Successfully negotiated waiver on financial covenant breach" }
      ],
      activeLoansData: [
        { 
          id: "HDL-2023-001", 
          type: "Term Loan", 
          amount: 500000000, 
          startDate: "2023-06-15", 
          endDate: "2028-06-14",
          interestRate: 8.75,
          remainingPrincipal: 450000000,
          nextPaymentDate: "2025-05-15",
          nextPaymentAmount: 25000000,
          repaymentSchedule: [
            { date: "2025-05-15", principal: 12500000, interest: 9843750, total: 22343750 },
            { date: "2025-06-15", principal: 12500000, interest: 9570313, total: 22070313 },
            { date: "2025-07-15", principal: 12500000, interest: 9296875, total: 21796875 }
          ]
        },
        { 
          id: "HDL-2023-002", 
          type: "Working Capital", 
          amount: 300000000, 
          startDate: "2023-10-01", 
          endDate: "2025-09-30",
          interestRate: 9.25,
          remainingPrincipal: 300000000,
          nextPaymentDate: "2025-05-01",
          nextPaymentAmount: 2312500,
          repaymentSchedule: [
            { date: "2025-05-01", principal: 0, interest: 2312500, total: 2312500 },
            { date: "2025-06-01", principal: 0, interest: 2312500, total: 2312500 },
            { date: "2025-07-01", principal: 0, interest: 2312500, total: 2312500 }
          ]
        },
        { 
          id: "HDL-2024-001", 
          type: "NCD", 
          amount: 400000000, 
          startDate: "2024-01-15", 
          endDate: "2029-01-14",
          interestRate: 8.50,
          remainingPrincipal: 400000000,
          nextPaymentDate: "2025-07-15",
          nextPaymentAmount: 17000000,
          repaymentSchedule: [
            { date: "2025-07-15", principal: 0, interest: 17000000, total: 17000000 },
            { date: "2026-01-15", principal: 0, interest: 17000000, total: 17000000 },
            { date: "2026-07-15", principal: 0, interest: 17000000, total: 17000000 }
          ]
        }
      ],
      pastLoansData: [
        {
          id: "HDL-2020-001",
          type: "Term Loan",
          amount: 200000000,
          startDate: "2020-04-01",
          endDate: "2023-03-31",
          interestRate: 10.25,
          status: "Fully Repaid",
          repaymentSchedule: [
            { date: "2023-01-01", principal: 16666667, interest: 1708333, total: 18375000 },
            { date: "2023-02-01", principal: 16666667, interest: 1137500, total: 17804167 },
            { date: "2023-03-31", principal: 16666667, interest: 569792, total: 17236459 }
          ]
        }
      ],
      reportsData: [
        { id: 1, name: "Quarterly Financial Statements Q1 2025", date: "2025-04-15", type: "Financial Statement", fileUrl: "#" },
        { id: 2, name: "Covenant Compliance Certificate", date: "2025-03-01", type: "Compliance", fileUrl: "#" },
        { id: 3, name: "Annual Financial Statements FY 2024", date: "2024-05-30", type: "Financial Statement", fileUrl: "#" }
      ]
    },
    { 
      id: 2, 
      name: "State Bank of India", 
      type: "Bank", 
      activeLoans: 2, 
      totalExposure: 800000000,
      keyContacts: [
        { name: "Amit Kumar", designation: "Senior Manager", email: "amit.k@sbi.co.in" },
        { name: "Neha Singh", designation: "Assistant GM", email: "neha.s@sbi.co.in" }
      ],
      recentEvents: [
        { date: "2025-01-20", description: "New term loan sanctioned for ₹400 million" }
      ],
      activeLoansData: [
        { 
          id: "SBI-2024-001", 
          type: "Term Loan", 
          amount: 400000000, 
          startDate: "2024-01-20", 
          endDate: "2029-01-19",
          interestRate: 8.90,
          remainingPrincipal: 390000000,
          nextPaymentDate: "2025-05-20",
          nextPaymentAmount: 20000000,
          repaymentSchedule: [
            { date: "2025-05-20", principal: 10000000, interest: 8691667, total: 18691667 },
            { date: "2025-06-20", principal: 10000000, interest: 8450000, total: 18450000 },
            { date: "2025-07-20", principal: 10000000, interest: 8216667, total: 18216667 }
          ]
        },
        { 
          id: "SBI-2023-001", 
          type: "Cash Credit", 
          amount: 400000000, 
          startDate: "2023-07-01", 
          endDate: "2025-06-30",
          interestRate: 9.50,
          remainingPrincipal: 400000000,
          nextPaymentDate: "2025-05-01",
          nextPaymentAmount: 3166667,
          repaymentSchedule: [
            { date: "2025-05-01", principal: 0, interest: 3166667, total: 3166667 },
            { date: "2025-06-01", principal: 0, interest: 3166667, total: 3166667 },
            { date: "2025-06-30", principal: 400000000, interest: 3166667, total: 403166667 }
          ]
        }
      ],
      pastLoansData: [],
      reportsData: [
        { id: 1, name: "Stock Statement March 2025", date: "2025-04-10", type: "Stock Statement", fileUrl: "#" },
        { id: 2, name: "Quarterly Financial Statements Q1 2025", date: "2025-04-15", type: "Financial Statement", fileUrl: "#" }
      ]
    },
    { 
      id: 3, 
      name: "Aditya Birla Capital", 
      type: "NBFC", 
      activeLoans: 1, 
      totalExposure: 350000000,
      keyContacts: [
        { name: "Vikram Mehta", designation: "VP Corporate Banking", email: "vikram.m@adityabirla.com" }
      ],
      recentEvents: [],
      activeLoansData: [
        { 
          id: "ABC-2024-001", 
          type: "Term Loan", 
          amount: 350000000, 
          startDate: "2024-02-15", 
          endDate: "2027-02-14",
          interestRate: 10.25,
          remainingPrincipal: 350000000,
          nextPaymentDate: "2025-05-15",
          nextPaymentAmount: 35350000,
          repaymentSchedule: [
            { date: "2025-05-15", principal: 29166667, interest: 2989583, total: 32156250 },
            { date: "2025-06-15", principal: 29166667, interest: 2739583, total: 31906250 },
            { date: "2025-07-15", principal: 29166667, interest: 2489583, total: 31656250 }
          ]
        }
      ],
      pastLoansData: [],
      reportsData: [
        { id: 1, name: "Quarterly Financial Statements Q1 2025", date: "2025-04-15", type: "Financial Statement", fileUrl: "#" }
      ]
    },
    { 
      id: 4, 
      name: "Franklin Templeton MF", 
      type: "Mutual Fund", 
      activeLoans: 1, 
      totalExposure: 500000000,
      keyContacts: [
        { name: "Sunita Rao", designation: "Fund Manager", email: "sunita.r@franklintempleton.com" }
      ],
      recentEvents: [
        { date: "2024-12-05", description: "Invested in 5-year NCD issue" }
      ],
      activeLoansData: [
        { 
          id: "FT-2024-001", 
          type: "NCD", 
          amount: 500000000, 
          startDate: "2024-12-05", 
          endDate: "2029-12-04",
          interestRate: 9.00,
          remainingPrincipal: 500000000,
          nextPaymentDate: "2025-06-05",
          nextPaymentAmount: 22500000,
          repaymentSchedule: [
            { date: "2025-06-05", principal: 0, interest: 22500000, total: 22500000 },
            { date: "2025-12-05", principal: 0, interest: 22500000, total: 22500000 },
            { date: "2026-06-05", principal: 0, interest: 22500000, total: 22500000 }
          ]
        }
      ],
      pastLoansData: [],
      reportsData: [
        { id: 1, name: "Rating Rationale Document", date: "2024-11-25", type: "Rating", fileUrl: "#" },
        { id: 2, name: "Due Diligence Report", date: "2024-11-15", type: "Due Diligence", fileUrl: "#" }
      ]
    }
  ];

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Filter lenders based on search query
  const filteredLenders = lenders.filter(
    lender => lender.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
              lender.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle adding a new lender
  const handleAddLoan = () => {
    toast.success("New lender added successfully");
    setShowAddLenderDialog(false);
  };
  
  // Get current lender data
  const currentLender = selectedLender !== null ? lenders.find(l => l.id === selectedLender) : null;
  
  // Handle back to lenders list
  const handleBackToLendersList = () => {
    setMainView("lenders");
    setSelectedLender(null);
  };
  
  // View lender details
  const handleViewLenderDetails = (lenderId: number) => {
    setSelectedLender(lenderId);
    setMainView("lender-details");
    setSelectedTab("contacts");
  };

  // Handle download report
  const handleDownloadReport = (reportId: number) => {
    toast.success("Report download started");
  };

  // Handle view report
  const handleViewReport = (reportId: number) => {
    toast.success("Opening report viewer");
  };

  // Generate compliance report
  const handleGenerateReport = () => {
    if (!currentLender) return;
    
    // In a real application, this would make an API call to generate the report
    const today = new Date();
    const reportName = `Monthly Lender Update - ${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`;
    
    // Create the generated report object
    const newReport = {
      id: Math.floor(Math.random() * 10000),
      name: reportName,
      date: today.toISOString().split('T')[0],
      type: "Compliance",
      fileUrl: "#",
      lenderId: currentLender.id
    };
    
    setGeneratedReport(newReport);
    setShowGenerateReportDialog(false);
    setShowFileReportDialog(true);
    
    toast.success("Report generated successfully");
  };
  
  // File the generated report
  const handleFileReport = () => {
    if (!currentLender || !generatedReport) return;
    
    // In a real application, this would make an API call to save the report
    const updatedLenders = lenders.map(lender => {
      if (lender.id === currentLender.id) {
        return {
          ...lender,
          reportsData: [
            ...lender.reportsData,
            {
              ...generatedReport,
              date: filingDate || new Date().toISOString().split('T')[0]
            }
          ]
        };
      }
      return lender;
    });
    
    // Reset states
    setShowFileReportDialog(false);
    setGeneratedReport(null);
    setFilingDate("");
    
    toast.success("Report filed successfully");
  };

  // Handle template file upload
  const handleTemplateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setTemplateFile(e.target.files[0]);
      toast.success(`Template file "${e.target.files[0].name}" selected`);
    }
  };

  // Handle template file submission
  const handleTemplateSubmit = () => {
    if (!templateFile || !currentLender) return;
    
    // In a real application, this would make an API call to upload the template
    toast.success(`Template file uploaded for ${currentLender.name}`);
    setShowUploadTemplateDialog(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                {mainView === "lenders" ? "Lender Relationship Management" : "Lender Details"}
              </CardTitle>
              <CardDescription>
                {mainView === "lenders"
                  ? "Manage relationships with banks, financial institutions and other lenders"
                  : currentLender ? `Viewing ${currentLender.name} details` : "View lender details"}
              </CardDescription>
            </div>
            {mainView === "lenders" ? (
              <Dialog open={showAddLenderDialog} onOpenChange={setShowAddLenderDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <PlusIcon className="mr-2 h-4 w-4" />
                    New Lender
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add New Lender</DialogTitle>
                    <DialogDescription>
                      Enter the details of the new lender. Click save when you're done.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="lender-name" className="text-right">
                        Name
                      </Label>
                      <Input id="lender-name" className="col-span-3" placeholder="Enter lender name" />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="lender-type" className="text-right">
                        Type
                      </Label>
                      <div className="col-span-3">
                        <Select>
                          <SelectTrigger>
                            <SelectValue placeholder="Select lender type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bank">Bank</SelectItem>
                            <SelectItem value="nbfc">NBFC</SelectItem>
                            <SelectItem value="mutual-fund">Mutual Fund</SelectItem>
                            <SelectItem value="private-credit">Private Credit</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="contact-person" className="text-right">
                        Key Contact
                      </Label>
                      <Input id="contact-person" className="col-span-3" placeholder="Primary contact name" />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="contact-email" className="text-right">
                        Email
                      </Label>
                      <Input id="contact-email" type="email" className="col-span-3" placeholder="Contact email" />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="designation" className="text-right">
                        Designation
                      </Label>
                      <Input id="designation" className="col-span-3" placeholder="Contact designation" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddLenderDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddLoan}>Save Lender</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <Button variant="outline" onClick={handleBackToLendersList}>
                Back to Lenders
              </Button>
            )}
          </div>
          {mainView === "lenders" && (
            <div className="mt-4">
              <Input
                placeholder="Search lenders by name or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md"
              />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {mainView === "lenders" ? (
            // Lenders List View
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lender</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Active Loans</TableHead>
                  <TableHead>Exposure</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLenders.map((lender) => (
                  <TableRow key={lender.id}>
                    <TableCell className="font-medium">{lender.name}</TableCell>
                    <TableCell>{lender.type}</TableCell>
                    <TableCell>{lender.activeLoans}</TableCell>
                    <TableCell>{formatCurrency(lender.totalExposure)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleViewLenderDetails(lender.id)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            // Lender Details View
            currentLender && (
              <div>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold">{currentLender.name}</h2>
                      <p className="text-muted-foreground">{currentLender.type} • {formatCurrency(currentLender.totalExposure)} total exposure</p>
                    </div>
                  </div>
                  
                  <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                    <TabsList className="grid grid-cols-5 mb-4">
                      <TabsTrigger value="contacts">Key Contacts</TabsTrigger>
                      <TabsTrigger value="active-loans">Active Loans</TabsTrigger>
                      <TabsTrigger value="past-loans">Past Loans</TabsTrigger>
                      <TabsTrigger value="reports">Reports</TabsTrigger>
                      <TabsTrigger value="events">Recent Events</TabsTrigger>
                    </TabsList>
                    
                    {/* Key Contacts Tab */}
                    <TabsContent value="contacts" className="space-y-4">
                      {currentLender.keyContacts.map((contact, idx) => (
                        <div key={idx} className="flex items-start gap-4 p-3 border rounded-md">
                          <div className="flex-1">
                            <div className="font-medium">{contact.name}</div>
                            <div className="text-sm text-muted-foreground">{contact.designation}</div>
                            <div className="text-sm mt-1">{contact.email}</div>
                          </div>
                          <Button variant="outline" size="sm">Contact</Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm">
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Add Contact
                      </Button>
                    </TabsContent>
                    
                    {/* Active Loans Tab */}
                    <TabsContent value="active-loans" className="space-y-6">
                      {currentLender.activeLoansData.length > 0 ? (
                        currentLender.activeLoansData.map((loan, idx) => (
                          <Card key={idx} className="overflow-hidden">
                            <CardHeader className="bg-muted/50 pb-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="text-lg flex items-center gap-2">
                                    <BanknotesIcon className="h-5 w-5" />
                                    {loan.id}: {loan.type}
                                  </CardTitle>
                                  <CardDescription>
                                    {formatCurrency(loan.amount)} at {loan.interestRate}% • {formatDate(loan.startDate)} to {formatDate(loan.endDate)}
                                  </CardDescription>
                                </div>
                                <div className="text-right">
                                  <div className="font-medium">Next Payment</div>
                                  <div className="text-sm text-muted-foreground">{formatDate(loan.nextPaymentDate)}</div>
                                  <div className="text-sm font-medium">{formatCurrency(loan.nextPaymentAmount)}</div>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <div className="text-sm text-muted-foreground mb-1">Loan Progress</div>
                                  <div className="flex items-center gap-2">
                                    <Progress 
                                      value={100 - (loan.remainingPrincipal / loan.amount * 100)} 
                                      className="h-2" 
                                    />
                                    <span className="text-sm">{Math.round(100 - (loan.remainingPrincipal / loan.amount * 100))}%</span>
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="flex justify-between mb-1">
                                    <span className="text-muted-foreground">Original Amount:</span>
                                    <span className="font-medium">{formatCurrency(loan.amount)}</span>
                                  </div>
                                  <div className="flex justify-between mb-1">
                                    <span className="text-muted-foreground">Remaining Principal:</span>
                                    <span className="font-medium">{formatCurrency(loan.remainingPrincipal)}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mt-4">
                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                  <CalendarIcon className="h-4 w-4" />
                                  Upcoming Repayments
                                </h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Principal</TableHead>
                                      <TableHead>Interest</TableHead>
                                      <TableHead>Total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {loan.repaymentSchedule.map((payment, payIdx) => (
                                      <TableRow key={payIdx}>
                                        <TableCell>{formatDate(payment.date)}</TableCell>
                                        <TableCell>{formatCurrency(payment.principal)}</TableCell>
                                        <TableCell>{formatCurrency(payment.interest)}</TableCell>
                                        <TableCell className="font-medium">{formatCurrency(payment.total)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                            <CardFooter className="bg-muted/50 py-3">
                              <div className="flex gap-2 ml-auto">
                                <Button variant="outline" size="sm">View Full Details</Button>
                                <Button size="sm">Process Payment</Button>
                              </div>
                            </CardFooter>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No active loans with this lender
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Past Loans Tab */}
                    <TabsContent value="past-loans" className="space-y-6">
                      {currentLender.pastLoansData.length > 0 ? (
                        currentLender.pastLoansData.map((loan, idx) => (
                          <Card key={idx}>
                            <CardHeader className="bg-muted/50 pb-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="text-lg flex items-center gap-2">
                                    <BanknotesIcon className="h-5 w-5" />
                                    {loan.id}: {loan.type}
                                  </CardTitle>
                                  <CardDescription>
                                    {formatCurrency(loan.amount)} at {loan.interestRate}% • {formatDate(loan.startDate)} to {formatDate(loan.endDate)}
                                  </CardDescription>
                                </div>
                                <div className="text-right">
                                  <div className="font-medium">Status</div>
                                  <div className="text-sm text-green-600 font-medium">{loan.status}</div>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="p-4">
                              <div className="mt-2">
                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                  <CalendarIcon className="h-4 w-4" />
                                  Final Repayments
                                </h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Principal</TableHead>
                                      <TableHead>Interest</TableHead>
                                      <TableHead>Total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {loan.repaymentSchedule.map((payment, payIdx) => (
                                      <TableRow key={payIdx}>
                                        <TableCell>{formatDate(payment.date)}</TableCell>
                                        <TableCell>{formatCurrency(payment.principal)}</TableCell>
                                        <TableCell>{formatCurrency(payment.interest)}</TableCell>
                                        <TableCell className="font-medium">{formatCurrency(payment.total)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                            <CardFooter className="bg-muted/50 py-3">
                              <div className="flex gap-2 ml-auto">
                                <Button variant="outline" size="sm">View Full Details</Button>
                              </div>
                            </CardFooter>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No past loans with this lender
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Reports Tab */}
                    <TabsContent value="reports">
                      {currentLender.reportsData.length > 0 ? (
                        <Card>
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-medium">Submitted Reports</h3>
                              <Button 
                                variant="outline" 
                                onClick={() => setShowUploadTemplateDialog(true)}
                              >
                                Upload Report Template
                              </Button>
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Report Name</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Filing Date</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {currentLender.reportsData.map((report) => (
                                  <TableRow key={report.id}>
                                    <TableCell className="font-medium">{report.name}</TableCell>
                                    <TableCell>{report.type}</TableCell>
                                    <TableCell>{formatDate(report.date)}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex justify-end gap-2">
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={() => handleViewReport(report.id)}
                                        >
                                          <ViewIcon className="h-4 w-4" />
                                          <span className="sr-only md:not-sr-only md:ml-2">View</span>
                                        </Button>
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={() => handleDownloadReport(report.id)}
                                        >
                                          <DownloadIcon className="h-4 w-4" />
                                          <span className="sr-only md:not-sr-only md:ml-2">Download</span>
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CardContent>
                          <CardFooter className="bg-muted/50 py-3">
                            <div className="flex gap-2 ml-auto">
                              <Button 
                                variant="default" 
                                size="sm"
                                onClick={() => setShowGenerateReportDialog(true)}
                              >
                                <FileCheckIcon className="mr-2 h-4 w-4" />
                                Generate Lender Update
                              </Button>
                              <Button size="sm">
                                <PlusIcon className="mr-2 h-4 w-4" />
                                Add New Report
                              </Button>
                            </div>
                          </CardFooter>
                        </Card>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground flex flex-col items-center gap-4">
                          <DocumentIcon className="h-10 w-10 text-muted-foreground/50" />
                          <div>No reports filed with this lender</div>
                          <div className="flex gap-4">
                            <Button 
                              onClick={() => setShowUploadTemplateDialog(true)}
                              variant="outline"
                            >
                              Upload Report Template
                            </Button>
                            <Button 
                              onClick={() => setShowGenerateReportDialog(true)}
                            >
                              <FileCheckIcon className="mr-2 h-4 w-4" />
                              Generate Lender Update
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Generate Report Dialog */}
                      <Dialog open={showGenerateReportDialog} onOpenChange={setShowGenerateReportDialog}>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Generate Lender Update</DialogTitle>
                            <DialogDescription>
                              Generate a monthly update report for {currentLender?.name}. This will create a standard report based on your template.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <div className="space-y-4">
                              <div className="bg-muted p-4 rounded-md">
                                <h4 className="font-medium mb-2 flex items-center">
                                  <FileCheckIcon className="mr-2 h-4 w-4" />
                                  Monthly Lender Update
                                </h4>
                                <ul className="space-y-2 text-sm">
                                  <li className="flex items-center">
                                    <ChevronRightIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Financial statements summary
                                  </li>
                                  <li className="flex items-center">
                                    <ChevronRightIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Covenant compliance status
                                  </li>
                                  <li className="flex items-center">
                                    <ChevronRightIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Outstanding loan summary
                                  </li>
                                  <li className="flex items-center">
                                    <ChevronRightIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Repayment history
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowGenerateReportDialog(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleGenerateReport}>
                              Generate Report
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* Template Upload Dialog */}
                      <Dialog open={showUploadTemplateDialog} onOpenChange={setShowUploadTemplateDialog}>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Upload Report Template</DialogTitle>
                            <DialogDescription>
                              Upload {currentLender?.name}'s reporting template. Our AI will use this to generate compliant reports.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4 space-y-4">
                            <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="template-file">Report Template</Label>
                              <Input 
                                id="template-file" 
                                type="file" 
                                onChange={handleTemplateUpload}
                                accept=".docx,.xlsx,.pdf"
                              />
                              <p className="text-sm text-muted-foreground">
                                Accepted formats: .docx, .xlsx, .pdf
                              </p>
                            </div>
                            {templateFile && (
                              <div className="bg-muted p-3 rounded-md">
                                <p className="text-sm font-medium">Selected file:</p>
                                <p className="text-sm">{templateFile.name}</p>
                              </div>
                            )}
                            <div>
                              <Label htmlFor="template-notes">Additional Notes (Optional)</Label>
                              <Textarea 
                                id="template-notes" 
                                placeholder="Add any specific instructions for using this template..." 
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowUploadTemplateDialog(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleTemplateSubmit} disabled={!templateFile}>
                              Upload Template
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* File Report Dialog */}
                      <Dialog open={showFileReportDialog} onOpenChange={setShowFileReportDialog}>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>File Lender Update</DialogTitle>
                            <DialogDescription>
                              Your report has been generated. Please confirm the filing date.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <div className="space-y-4">
                              <div className="bg-muted p-4 rounded-md mb-4">
                                <h4 className="font-medium">{generatedReport?.name}</h4>
                                <p className="text-sm text-muted-foreground">Generated on {generatedReport ? formatDate(generatedReport.date) : ''}</p>
                              </div>
                              <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="filing-date" className="text-right">
                                  Filing Date
                                </Label>
                                <Input 
                                  id="filing-date" 
                                  type="date" 
                                  value={filingDate} 
                                  onChange={(e) => setFilingDate(e.target.value)}
                                  className="col-span-3" 
                                />
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowFileReportDialog(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleFileReport}>
                              File Report
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </TabsContent>
                    
                    {/* Recent Events Tab */}
                    <TabsContent value="events">
                      {currentLender.recentEvents.length > 0 ? (
                        <div className="space-y-3">
                          {currentLender.recentEvents.map((event, idx) => (
                            <div key={idx} className="border-l-4 border-primary pl-4 py-2">
                              <div className="text-sm text-muted-foreground">
                                {formatDate(event.date)}
                              </div>
                              <div>{event.description}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No recent events recorded
                        </div>
                      )}
                      <div className="mt-4">
                        <Button variant="outline" size="sm">
                          <PlusIcon className="mr-2 h-4 w-4" />
                          Add Event
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};
